import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer, Waves, Trophy } from "lucide-react";
import { DashboardSkeleton, LoadError } from "@/components/PageState";

export default function ReportCard() {
  const { studentId } = useParams();
  const [r, setR] = useState(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/reports/${studentId}`);
        setR(data);
      } catch (requestError) { setError(requestError?.response?.data?.detail || "Report card could not load."); }
    })();
  }, [studentId, retry]);

  if (error) return <LoadError message={error} onRetry={()=>{ setError(""); setRetry((value)=>value+1); }} testId="report-load-error" />;
  if (!r) return <DashboardSkeleton testId="report-loading" cards={4} />;

  const gradeFromAvg = (avg) => {
    if (avg == null) return "—";
    if (avg >= 90) return "A";
    if (avg >= 80) return "B";
    if (avg >= 70) return "C";
    if (avg >= 60) return "D";
    return "In Progress";
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="report-card">
      <div className="print:hidden flex items-center justify-between mb-6">
        <div>
          <div className="chip mb-2">Report</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Report card</h1>
          <p className="text-slate-500 text-sm">Generated {new Date(r.generated_at).toLocaleDateString()}</p>
        </div>
        <button data-testid="report-print" onClick={() => window.print()} className="px-5 py-2.5 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold flex items-center gap-2">
          <Printer size={16} /> Print / save PDF
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-3xl p-8 print:border-0 print:shadow-none print:rounded-none">
        <header className="flex items-center justify-between border-b border-slate-200 pb-4 mb-6">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center w-10 h-10 rounded-2xl bg-teal-700 text-white"><Waves size={18} /></span>
            <div>
              <div className="font-display text-xl font-bold text-slate-900">Oceanic Learn</div>
              <div className="text-xs text-slate-500">Homeschool progress report</div>
            </div>
          </div>
          <div className="text-right text-sm">
            <div className="font-semibold text-slate-900">{r.student.name}</div>
            <div className="text-slate-500">{r.student.email}</div>
          </div>
        </header>

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          <Kpi label="Curriculum lessons completed" value={`${r.metrics.curriculum_lessons_completed}/${r.metrics.curriculum_lessons_total}`} testId="report-curriculum-lessons" />
          <Kpi label="Assignments turned in" value={r.metrics.assignments_turned_in} testId="report-assignments-turned-in" />
          <Kpi label="Assignments graded" value={r.metrics.assignments_graded} testId="report-assignments-graded" />
          <Kpi label="Assignment records" value={r.metrics.assignments_total} testId="report-assignment-count" />
        </section>

        <section className="mb-8">
          <h2 className="font-display text-lg font-semibold text-slate-900 mb-3">Subject progress</h2>
          <div className="overflow-x-auto"><table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-2">Subject</th><th>Lessons</th><th>Avg score</th><th>Mark</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(r.by_subject).map(([slug, s]) => (
                <tr key={slug} className="border-b border-slate-100">
                  <td className="py-2 text-slate-900">{s.title}</td>
                  <td>{s.lessons_done} / {s.lessons_total}</td>
                  <td>{s.avg_score != null ? `${s.avg_score}%` : "—"}</td>
                  <td className="font-semibold text-teal-800">{gradeFromAvg(s.avg_score)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </section>

        <section className="mb-8">
          <h2 className="font-display text-lg font-semibold text-slate-900 mb-1">Assignments</h2>
          <div className="text-xs text-slate-500 mb-3" data-testid="report-assignment-record-label">All {r.assignments.length} teacher-created assignments (archived items included — archiving never changes grades)</div>
          {r.assignments.length === 0 ? <div className="text-sm text-slate-500">None yet.</div> : (
            <ul className="text-sm space-y-1">
              {r.assignments.map((a) => (
                <li key={a.id} data-testid={`report-assignment-${a.id}`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 border-b border-slate-100 py-2">
                  <span>{a.title} · <span className="text-slate-500">{a.subject}</span></span>
                  <span className="font-semibold capitalize">{a.grade != null && String(a.grade) !== "" ? String(a.grade) : a.status?.replaceAll("_", " ")}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h2 className="font-display text-lg font-semibold text-slate-900 mb-3">Achievements</h2>
          <div className="flex flex-wrap gap-2">
            {r.achievements.map((a) => (
              <span key={a.id} className="chip"><Trophy size={12} /> {a.name}</span>
            ))}
            {r.achievements.length === 0 && <span className="text-sm text-slate-500">No badges yet.</span>}
          </div>
        </section>

        <footer className="mt-10 pt-4 border-t border-slate-200 text-xs text-slate-400">
          Generated by Oceanic Learn · {new Date(r.generated_at).toLocaleString()}
        </footer>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 16mm; }
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          nav, aside, header { display: none !important; }
          main { padding: 0 !important; }
        }
      `}</style>
    </div>
  );
}

function Kpi({ label, value, testId }) {
  return (
    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
      <div className="text-xs text-slate-500 uppercase tracking-widest">{label}</div>
      <div data-testid={testId} className="font-display text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}
